import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_LOCAL_URL } from "../../config";
import { Search, Filter, ChevronLeft, ChevronRight, Clock, Shield, ShieldAlert, Globe } from "lucide-react";

export default function AuditPage({currentUser}) {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    username: "",
    action: "",
    module: "",
    startDate: "",
    endDate: ""
  });

  useEffect(() => {
    if (currentUser?.role === 'ADMIN'){
    fetchLogs();
    }
  }, [page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_LOCAL_URL}/api/admin/audits`, {
        params: { page, size: 20, ...filters },
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setLogs(res.data.content);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("Audit fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  // RESTRICTION CHECK
  if (currentUser?.role !== 'ADMIN') {
    return (
      <div className="card border-0 shadow-sm p-5 text-center rounded-4 mt-4">
        <div className="bg-warning bg-opacity-10 p-4 rounded-circle d-inline-block mb-3">
          <ShieldAlert size={48} className="text-warning" />
        </div>
        <h4 className="fw-bold text-dark">Administrative Access Required</h4>
        <p className="text-muted mx-auto" style={{ maxWidth: '400px' }}>
          Your current access level does not permit viewing system audit logs. 
        </p>
      </div>
    );
  }

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const applyFilters = () => {
    setPage(0);
    fetchLogs();
  };

  const getActionBadge = (action) => {
    const base = "badge rounded-pill fw-bold px-3 py-1 ";
    switch (action) {
      case 'CREATE': return base + "bg-success-subtle text-success border border-success-subtle";
      case 'UPDATE': return base + "bg-primary-subtle text-primary border border-primary-subtle";
      case 'DELETE': return base + "bg-danger-subtle text-danger border border-danger-subtle";
      default: return base + "bg-secondary-subtle text-secondary";
    }
  };

  return (
    <div className="container-fluid py-4 px-lg-5 bg-white min-vh-100">
      {/* PAGE HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold text-dark mb-1">Audit Logs</h4>
        </div>
      </div>

      {/* FILTER BAR - Compact Enterprise Design */}
      <div className="card border-0 shadow-sm rounded-3 mb-4 bg-light bg-opacity-50">
        <div className="card-body p-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-2">
              <label className="tiny-text fw-bold text-muted mb-1 text-uppercase">Admin User</label>
              <input name="username" className="form-control form-control-sm border-0 shadow-xs" placeholder="Filter user..." onChange={handleFilterChange} />
            </div>
            <div className="col-md-2">
              <label className="tiny-text fw-bold text-muted mb-1 text-uppercase">Action Type</label>
              <select name="action" className="form-select form-select-sm border-0 shadow-xs" onChange={handleFilterChange}>
                <option value="">All Actions</option>
                <option>CREATE</option>
                <option>UPDATE</option>
                <option>DELETE</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="tiny-text fw-bold text-muted mb-1 text-uppercase">Module</label>
              <input name="module" className="form-control form-control-sm border-0 shadow-xs" placeholder="e.g. Products" onChange={handleFilterChange} />
            </div>
            <div className="col-md-2">
              <label className="tiny-text fw-bold text-muted mb-1 text-uppercase">Date From</label>
              <input type="datetime-local" name="startDate" className="form-control form-control-sm border-0 shadow-xs" onChange={handleFilterChange} />
            </div>
            <div className="col-md-2">
              <label className="tiny-text fw-bold text-muted mb-1 text-uppercase">Date To</label>
              <input type="datetime-local" name="endDate" className="form-control form-control-sm border-0 shadow-xs" onChange={handleFilterChange} />
            </div>
            <div className="col-md-2">
              <button className="btn btn-sm btn-dark w-100 fw-bold rounded-2 shadow-sm py-2" onClick={applyFilters}>
                <Filter size={14} className="me-2" /> Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* AUDIT TABLE */}
      <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle mb-0" style={{ fontSize: '0.85rem' }}>
            <thead className="bg-light text-uppercase tiny-text fw-bold text-muted">
              <tr>
                <th className="px-4 py-3"><Clock size={14} className="me-1"/> Event Timestamp</th>
                <th className="py-3">User Context</th>
                <th className="py-3 text-center">Action</th>
                <th className="py-3">Module Affected</th>
                <th className="py-3">Status</th>
                <th className="py-3 text-end px-4"><Globe size={14} className="me-1"/> Client IP</th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {loading ? (
                <tr><td colSpan="6" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-5 text-muted">No audit events found.</td></tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-bottom-subtle">
                    <td className="px-4 text-muted">{log.createdAt}</td>
                    <td>
                      <div className="fw-bold text-dark">{log.username}</div>
                      <div className="extra-small text-muted fw-semibold">{log.role}</div>
                    </td>
                    <td className="text-center">
                      <span className={getActionBadge(log.action)}>{log.action}</span>
                    </td>
                    <td className="fw-medium text-secondary">{log.module}</td>
                    <td>
                      <span className={`badge ${log.status === "SUCCESS" ? "bg-success" : "bg-danger"}`} style={{ fontSize: '0.65rem', padding: '0.35em 0.8em' }}>
                        {log.status}
                      </span>
                    </td>
                    <td className="text-end px-4 text-muted font-monospace" style={{ fontSize: '0.75rem' }}>{log.ipAddress}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ENTERPRISE PAGINATION */}
      <div className="d-flex justify-content-between align-items-center mt-4 px-2">
        <div className="text-muted small fw-medium">
          Showing Page <span className="text-dark fw-bold">{page + 1}</span> of <span className="text-dark fw-bold">{totalPages}</span>
        </div>
        <div className="btn-group shadow-xs rounded-3 overflow-hidden">
          <button disabled={page === 0} onClick={() => setPage(page - 1)} className="btn btn-white border px-3 py-2 bg-white text-dark small">
            <ChevronLeft size={16} /> Prev
          </button>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} className="btn btn-white border px-3 py-2 bg-white text-dark small">
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <style>{`
        .tiny-text { font-size: 0.65rem; letter-spacing: 0.8px; }
        .extra-small { font-size: 0.7rem; }
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .border-bottom-subtle { border-bottom: 1px solid rgba(0,0,0,0.03); }
        .btn-white:hover:not(:disabled) { background: #f8f9fa !important; }
        .font-monospace { font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace; }
      `}</style>
    </div>
  );
}